import { FormikErrors } from "formik";
import { LexicalEditor } from "lexical";
import { FeatureFlagReturnType } from "src/components/Providers/FeatureFlagProvider";
import {
  editorStateToPlainText,
  isEmptyLexicalState,
  serializedEditorStateToHtml,
  serializedEditorStateToPlainText,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { $validateEditorState } from "src/components/WysiwygEditor/lexical/utils/validator";
import { MESSAGE_CONTENT_LIMIT } from "src/constants";
import { toSegmentedMessage } from "src/hooks/useSegmentedMessage";
import { mustacheSafeParse } from "src/services/mustache";
import { FormikErrorAccumulator } from "src/types/FormikErrorsAccumulator";
import { AutomaticMessageFormType, MessageTemplateContent } from "./schemas";

export const validateMessage = async (
  message: MessageTemplateContent,
  emailEditor: LexicalEditor | undefined,
  smsEditor: LexicalEditor | undefined,
  featureFlag: FeatureFlagReturnType
) => {
  const formikError: FormikErrors<MessageTemplateContent> = {};

  if (!message.subject) {
    formikError.subject = "Email subject is required.";
  } else {
    const subjectError = mustacheSafeParse(
      message.subject,
      "One or more variables in the email subject line are invalid. Correct or remove them and try again."
    )[0];
    if (subjectError) formikError.subject = subjectError;
  }

  if (!message.lexical) {
    formikError.lexical = "Email content is required.";
  }

  if (emailEditor && message.lexical) {
    const emailEditorState = emailEditor.parseEditorState(message.lexical);
    if (isEmptyLexicalState(emailEditorState)) {
      formikError.lexical = "Email content is required.";
    } else if (!$validateEditorState(emailEditorState)) {
      formikError.lexical =
        "One or more variables in the email content are invalid. Correct or remove them and try again.";
    } else {
      const htmlFromMessage = serializedEditorStateToHtml(
        emailEditor,
        message.lexical
      );
      const lexicalError = mustacheSafeParse(
        htmlFromMessage,
        "One or more variables in the email content are invalid. Correct or remove them and try again."
      )[0];
      if (lexicalError) formikError.lexical = lexicalError;
    }
  }

  if (!message.smsLexical) {
    formikError.smsLexical = "SMS content is required.";
  }

  if (smsEditor && message.smsLexical) {
    const isSegmentedMessageWarningEnabled =
      featureFlag["segmented_message_warning"].enabled;

    const smsEditorState = smsEditor.parseEditorState(message.smsLexical);
    const plainTextSms =
      serializedEditorStateToPlainText(smsEditor, message.smsLexical) ??
      editorStateToPlainText(smsEditorState);
    const segmentedMessage = toSegmentedMessage(plainTextSms, featureFlag);
    if (isEmptyLexicalState(smsEditorState)) {
      formikError.smsLexical = "SMS content is required.";
    } else if (
      isSegmentedMessageWarningEnabled &&
      segmentedMessage.isOverflowing()
    ) {
      formikError.smsLexical = `SMS message exceeds character limit.`;
    } else if (
      !isSegmentedMessageWarningEnabled &&
      segmentedMessage.numberOfCharacters > MESSAGE_CONTENT_LIMIT
    ) {
      formikError.smsLexical = `SMS message must be less than ${MESSAGE_CONTENT_LIMIT} characters.`;
    } else if (!$validateEditorState(smsEditorState)) {
      formikError.smsLexical =
        "One or more variables in the SMS message are invalid. Correct or remove them and try again.";
    } else {
      const smsError = mustacheSafeParse(
        plainTextSms,
        "One or more variables in the SMS message are invalid. Correct or remove them and try again."
      )[0];
      if (smsError) formikError.smsLexical = smsError;
    }
  }
  return formikError;
};

export const validateMessages = async (
  values: AutomaticMessageFormType,
  emailEditor: LexicalEditor | undefined,
  smsEditor: LexicalEditor | undefined,
  featureFlag: FeatureFlagReturnType,
  targetLanguages?: string[]
) => {
  const formikError = new FormikErrorAccumulator<AutomaticMessageFormType>();
  for (let idx = 0; idx < values.messages.length; idx++) {
    const message = values.messages[idx];
    if (
      !message ||
      (targetLanguages && !targetLanguages.includes(message.language ?? "en"))
    ) {
      continue;
    }
    const errors = await validateMessage(
      message,
      emailEditor,
      smsEditor,
      featureFlag
    );
    if (Object.keys(errors).length > 0) {
      const errorMessages = values.messages.map(
        (m, i): FormikErrors<MessageTemplateContent> =>
          i === idx ? errors : {}
      );
      formikError.add({
        messages: errorMessages,
      });
      break;
    }
  }
  return { errors: formikError.errors };
};
